import React, { useEffect, useState } from "react";

// region External libraries
import _ from "lodash";
import { SpeedDial, SpeedDialMenu, useToast } from "@npm-telluria-tecnologia/telluria-ui";
import { Add } from "@mui/icons-material";
import { atom, useAtom } from "jotai";
import { useLazyQuery } from "@apollo/client";
import { useMsal } from "@azure/msal-react";
import { matchesSubstringIgnoreCaseAndAccents } from "@npm-telluria-tecnologia/telluria-utils";
// endregion External libraries
// region Hooks
import { useIdentity, PERMISSIONS } from "@hooks/useIdentity";
// endregion Hooks
// region Languages
import useTranslation from "@languages/useTranslation";
import { UserRightsMessages } from "@languages/interfaces/userRightsMessages";
import { GlobalMessages } from "src/languages/interfaces";
// endregion Languages
// region Helpers
import { addTab } from "@helpers/tabsUtils";
import { groupUsersByRoleId, IRoleWithUsers, sortAlphabetically, sortByBoolean } from "src/helpers/utils";
// endregion Helpers
// region Store
import { TUserAllWithPaginationQueries, userAllWithPagination } from "src/store/graphql/groups/user/query.graphql";
import {
  ECommandResultStatus,
  EWhereClausesOperators,
  RoleQueriesGetAllWithPaginationArgs,
  RoleType,
  UserQueriesGetAllWithPaginationArgs,
  UserType
} from "src/store/graphql/helpers/types.graphql";
import { roleAllWithPagination, TRoleAllWithPaginationQueries } from "src/store/graphql/groups/role/query.graphql";
// endregion Store
// region Atoms
import PageTitle from "@atoms/PageTitle";
import UserProfile from "@atoms/UserProfile";
import Loading from "@atoms/Loading";
import SearchField from "@atoms/SearchField";
import SelectIdentityModule, { selectedModuleIdAtom } from "@atoms/SelectIdentityModule";
import AccessDenied from "src/atoms/AccessDenied";
// endregion Atoms
// region Molecules
import RoleCard from "src/molecules/RoleCard";
// endregion Molecules
// region Organisms
import { PageTabProps, useTabsAtom } from "@organisms/PageTabs";
// endregion Organisms
// region Pages
import { selectedTabAtom, userRightsTabsAtom } from "@pages/UsersRights";
import Role from "@pages/Role";
import User from "@pages/User";
// endregion Pages
// region Styles
import theme from "src/styles/themes/light";

import * as Styled from "./styles";
// endregion Styles

interface IUserRightsDashboardProps {
  tabPanelIndex?: number | null;
}

const UserRightsDashboard: React.FC<IUserRightsDashboardProps> = ({ tabPanelIndex }) => {
  const { validateUserAccess } = useIdentity();
  // region queries
  const [QueryGetUserAllWithPagination] = useLazyQuery<TUserAllWithPaginationQueries,
    UserQueriesGetAllWithPaginationArgs>(
      userAllWithPagination([
        "id", "name", "email", "position", "imageUrl", "admin", "active", "userRoles.roleId",
        "userRoles.role.moduleId", "userRoles.role.module.name"
      ]),
      { fetchPolicy: "network-only" }
    );

  const [QueryGetRoleAllWithPagination] = useLazyQuery<TRoleAllWithPaginationQueries,
    RoleQueriesGetAllWithPaginationArgs>(
      roleAllWithPagination([
        "id", "name", "description", "moduleId"
      ]),
      { fetchPolicy: "network-only" }
    );
  // endregion queries

  // region hooks
  const { t } = useTranslation();
  const { addToast } = useToast();
  const { instance } = useMsal();
  const [selectedModuleId] = useAtom(selectedModuleIdAtom);
  const { selectedTab, setSelectedTab, addNewTab } = useTabsAtom(userRightsTabsAtom, selectedTabAtom);
  // endregion hooks

  // region states
  const [isLoading, setIsLoading] = useState(false);
  const [roleWithUsers, setRoleWithUsers] = useState<IRoleWithUsers[]>([]);
  const [displayedRoleWithUsers, setDisplayedRoleWithUsers] = useState<IRoleWithUsers[]>([]);
  const [openSpeedDialActionMenus, setOpenSpeedDialActionMenus] = useState(false);
  const [hasAccessPermission, setHasAccessPermission] = useState(false);
  const [hasUserIncludePermission, setHasUserIncludePermission] = useState(false);
  const [hasGroupIncludePermission, setHasGroupIncludePermission] = useState(false);
  // endregion states

  // region constants
  const actionMenuTabs: PageTabProps[] = [
    {
      id: "groups",
      tab: {
        title: UserRightsMessages.userControl,
        caption: UserRightsMessages.createRoleTabCaption
      },
      tabPanel: <Role selectedStepTabAtom={atom<number>(0)} mode="CREATE" />
    },
    {
      id: "users",
      tab: {
        title: UserRightsMessages.userControl,
        caption: UserRightsMessages.createUserTabCaption
      },
      tabPanel: <User selectedStepTabAtom={atom<number>(0)} mode="CREATE" />
    }
  ];

  const isSelectedTab = tabPanelIndex === selectedTab;
  const speedDialActionMenus = actionMenuTabs.map(({ id, tab: { caption } }) => ({ id, caption }));
  // endregion constants

  // region Speed dial action menus handlers
  const handleOpenActionMenusSpeedDial = () => { setOpenSpeedDialActionMenus(true); };
  const handleCloseActionMenusSpeedDial = () => { setOpenSpeedDialActionMenus(false); };
  // endregion Speed dial action menus handlers

  // region functions
  const addActionMenuTab = (id: string) => {
    handleCloseActionMenusSpeedDial();

    const result = addTab(addNewTab, actionMenuTabs, id);

    setSelectedTab(result);
  };

  const filterDataOnScreen = (termForFilteringDataOnScreen: string) => {
    if (!termForFilteringDataOnScreen) {
      setDisplayedRoleWithUsers(roleWithUsers);

      return;
    }

    const match = (propValue: string) => {
      const formattedPropValue = propValue ?? "";

      return matchesSubstringIgnoreCaseAndAccents(formattedPropValue.toString(), termForFilteringDataOnScreen);
    };

    const cloneRoleWithUsers = _.cloneDeep(roleWithUsers);

    const filteredRoleWithUsers = cloneRoleWithUsers.filter((roleWithUsers) => (
      match(roleWithUsers.name) || match(roleWithUsers.users?.map((user) => user.name).join(" "))
    ));

    setDisplayedRoleWithUsers(filteredRoleWithUsers);
  };

  const getRoles = async () => {
    try {
      const query = await QueryGetRoleAllWithPagination({
        variables: {
          where: [
            {
              property: "moduleId",
              operator: EWhereClausesOperators.EQUALS,
              value: selectedModuleId
            }
          ]
        }
      });

      const response = query.data?.role.getAllWithPagination;

      if (response?.status !== ECommandResultStatus.SUCCESS) return [] as RoleType[];

      return response.result as RoleType[];
    } catch (error) {
      addToast({ type: "error", title: "Internal client error", description: error.message });

      return [] as RoleType[];
    }
  };

  const getUsers = async () => {
    try {
      const query = await QueryGetUserAllWithPagination();

      const response = query.data?.user.getAllWithPagination;

      if (response?.status !== ECommandResultStatus.SUCCESS) return [] as UserType[];

      return response.result as UserType[];
    } catch (error) {
      addToast({ type: "error", title: "Internal client error", description: error.message });

      return [] as UserType[];
    }
  };
  // endregion functions

  useEffect(() => {
    (async () => {
      if (!selectedModuleId || !isSelectedTab) return;

      setIsLoading(true);

      const users = await getUsers();
      const roles = await getRoles();

      const isCurrentUserAdmin = instance.getActiveAccount()?.idTokenClaims?.extension_Admin;

      const sortedAlphabeticallyUsers = sortAlphabetically(users, "name");
      const sortedUsers = sortByBoolean(sortedAlphabeticallyUsers, "active");

      const groupedUsers = groupUsersByRoleId(
        isCurrentUserAdmin ? sortedUsers : sortedUsers.filter((user) => !user.admin),
        roles,
        selectedModuleId
      );

      setRoleWithUsers(groupedUsers);
      setDisplayedRoleWithUsers(groupedUsers);
      setIsLoading(false);
    })();

    return () => {
      setRoleWithUsers([]);
      setDisplayedRoleWithUsers([]);
    };
  }, [selectedModuleId, isSelectedTab]);

  // region Permission Effects
  useEffect(() => {
    const canAccess = validateUserAccess(PERMISSIONS.USER_RIGHTS_CONTROL.USERS.ACCESS)
      || validateUserAccess(PERMISSIONS.USER_RIGHTS_CONTROL.GROUPS.ACCESS);

    setHasAccessPermission(canAccess);
    setHasUserIncludePermission(validateUserAccess(PERMISSIONS.USER_RIGHTS_CONTROL.USERS.INCLUDE));
    setHasGroupIncludePermission(validateUserAccess(PERMISSIONS.USER_RIGHTS_CONTROL.GROUPS.INCLUDE));
  }, [validateUserAccess]);
  // endregion Permission Effects

  if (!hasAccessPermission) {
    return <AccessDenied />;
  }

  return (
    <Styled.Container>
      <Loading loading={isLoading} />
      <Styled.Header>
        <Styled.HeaderTitle>
          <PageTitle>{t(UserRightsMessages.roleDashboardPageTitle)}</PageTitle>
          <SelectIdentityModule />
        </Styled.HeaderTitle>
        <Styled.HeaderFilters>
          <SearchField onSearchClick={filterDataOnScreen} onClearClick={() => filterDataOnScreen("")} />
        </Styled.HeaderFilters>
      </Styled.Header>
      <Styled.Board>
        {displayedRoleWithUsers.length === 0 && !isLoading && selectedModuleId !== null ? (
          <Styled.NoRecordsFound>
            <span>{selectedModuleId
              ? (roleWithUsers.length === 0 ? t(UserRightsMessages.noRoleOrUserRegistered) : t(GlobalMessages.noRecordsFoundText))
              : t(UserRightsMessages.noSuiteAllowed)}
            </span>
          </Styled.NoRecordsFound>
        ) : (
          displayedRoleWithUsers.map((role) => (
            <RoleCard key={role.id} currentRole={role}>
              {role.users?.map((user) => (
                <UserProfile key={user.id} currentUser={user} />
              ))}
            </RoleCard>
          ))
        )}
      </Styled.Board>
      {!_.isEmpty(selectedModuleId) && (
        <SpeedDial
          primaryColor={theme.brand.navy}
          secondColor={theme.brand.navyMed}
          size="large"
          icon={<Add fontSize="large" />}
          open={openSpeedDialActionMenus}
          onOpen={handleOpenActionMenusSpeedDial}
          onClose={handleCloseActionMenusSpeedDial}
        >
          {speedDialActionMenus.slice(0).reverse().map((menu) => {

            const menuWithTranslate = { id: menu.id, title: t(menu.caption) };

            let noPermission = false;

            if (menu.id === "users" && !hasUserIncludePermission) {
              noPermission = true;
            } else if (menu.id === "groups" && !hasGroupIncludePermission) {
              noPermission = true;
            }

            const onClick = () => {
              if (noPermission) {
                return;
              }

              addActionMenuTab(menuWithTranslate.id);
            };

            return (
              <Styled.SpeedDialButtonContainer key={menuWithTranslate.id} className={noPermission ? "no-permission" : ""}>
                <SpeedDialMenu
                  key={menuWithTranslate.id}
                  menu={menuWithTranslate}
                  colorOnHover={theme.brand.navyMed}
                  onClick={onClick}
                />
              </Styled.SpeedDialButtonContainer>
            );
          })}
        </SpeedDial>
      )}
    </Styled.Container>
  );
};

UserRightsDashboard.defaultProps = {
  tabPanelIndex: null
};

export default UserRightsDashboard;
